import { forwardRef, useMemo } from 'react';
import { TextField, InputAdornment, IconButton, Box } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import searchIconSvg from '../../assets/icons/searchIcon.svg';

const SearchInput = forwardRef(
  (
    {
      placeholder,
      value,
      onChange,
      onKeyDown,
      onClear,
      isSearching = false,
      fullWidth = true,
      size = 'small',
      icon = <img src={searchIconSvg} alt="Search" />,
      iconPosition = 'start',
      minWidth: minWidthProp = 120,
      // Optional font size for the input text + placeholder — lets callers with
      // longer placeholders shrink the text so it isn't clipped.
      fontSize,
      // Optional small caption rendered below the input (MUI's own
      // FormHelperText slot) — callers use this to name which columns the
      // search actually matches, instead of cramming that into the
      // placeholder where it risks getting clipped in a narrow box.
      helperText,
    },
    ref
  ) => {
    // Calculate width based on placeholder text length
    const calculatedWidth = useMemo(() => {
      if (!placeholder || fullWidth) return undefined;
      // Approximate width: ~8px per character + padding for icons and borders
      const charCount = placeholder.length;
      const iconPadding = iconPosition === 'start' ? 40 : 0;
      const clearButtonPadding = value && onClear ? 40 : 0;
      const borderPadding = 32; // Left and right padding
      const calculated =
        charCount * 8 + iconPadding + clearButtonPadding + borderPadding;
      return Math.max(calculated, minWidthProp);
    }, [placeholder, fullWidth, iconPosition, value, onClear, minWidthProp]);

    return (
      <Box sx={{ width: fullWidth ? '100%' : calculatedWidth }}>
        <TextField
          ref={ref}
          fullWidth={fullWidth}
          size={size}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          helperText={helperText}
          FormHelperTextProps={{ sx: { mx: 0, color: '#6B7280' } }}
          sx={!fullWidth ? { width: '100%' } : undefined}
          InputProps={{
            startAdornment: iconPosition === 'start' && (
              <InputAdornment position={iconPosition}>{icon}</InputAdornment>
            ),
            endAdornment: iconPosition === 'end' && value && onClear && (
              <InputAdornment position={iconPosition}>
                <IconButton
                  size="small"
                  onClick={onClear}
                  sx={{
                    color: '#9ca3af',
                    '&:hover': { color: '#6b7280' },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'white',
              ...(fontSize
                ? { '& .MuiInputBase-input': { fontSize } }
                : {}),
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isSearching ? '#F1AC2F' : '#d1d5db',
                transition: 'border-color 0.2s ease-in-out',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isSearching ? '#F1AC2F' : '#9ca3af',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#486791',
              },
            },
          }}
        />
      </Box>
    );
  }
);

export default SearchInput;
